import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from '@/components/ui';
import { Link } from 'react-router-dom';
import ResponsiveModal from '../../../components/ResponsiveModal';
import GoogleGPSMap from '../../../components/GoogleGPSMap';
import { assetService } from '../../../services/assetService';
import {
  fetchPcrPromoteContext,
  patchPcrSubmissionStatus,
  promotePcrToWorkId,
  type PcrPromoteContextResponse,
  type PcrSubmission,
} from '../../../services/pcrService';
import type { Asset } from '../../../types/Asset';
import { ASSET_TYPES, ASSET_TYPE_LABELS } from '../../../constants/assetTypes';

export interface PcrPromoteToWorkIdModalProps {
  visible: boolean;
  row: PcrSubmission | null;
  onClose: () => void;
  onSuccess: (args: { submission: PcrSubmission; workIdId: number; workIdentificationId: string | null }) => void;
}

type Step = 'form' | 'statusPrompt';

const PcrPromoteToWorkIdModal: React.FC<PcrPromoteToWorkIdModalProps> = ({
  visible,
  row,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>('form');
  const [promoteResult, setPromoteResult] = useState<{
    submission: PcrSubmission;
    workIdId: number;
    workIdentificationId: string | null;
  } | null>(null);
  const [applyingStatus, setApplyingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [context, setContext] = useState<PcrPromoteContextResponse | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [foremanAreaId, setForemanAreaId] = useState<number | ''>('');
  const [comments, setComments] = useState('');
  const [priority, setPriority] = useState('');
  const [assetType, setAssetType] = useState<string>(ASSET_TYPES.ROAD);
  const [assetId, setAssetId] = useState<number | ''>('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showMap, setShowMap] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedForeman = context?.filterOptions.foremanAreas.find((fa) => fa.id === foremanAreaId);
  const serviceAreaId = selectedForeman?.serviceAreaId;

  useEffect(() => {
    if (!visible || !row) {
      setContext(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingContext(true);
      setError(null);
      try {
        const ctx = await fetchPcrPromoteContext(row.id);
        if (cancelled) return;
        setContext(ctx);
        if (!ctx.canPromote && ctx.reasonBlocked) {
          setError(ctx.reasonBlocked);
        }
        setComments(ctx.defaultComments);
        setPriority(ctx.defaultPriority);
        if (ctx.suggestedForemanAreaId) setForemanAreaId(ctx.suggestedForemanAreaId);
        if (ctx.suggestedAssetId) setAssetId(ctx.suggestedAssetId);
        if (ctx.suggestedLatitude != null && ctx.suggestedLongitude != null) {
          setLocation({ lat: ctx.suggestedLatitude, lng: ctx.suggestedLongitude });
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load form');
      } finally {
        if (!cancelled) setLoadingContext(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, row]);

  useEffect(() => {
    if (!serviceAreaId || !foremanAreaId || !visible) {
      setAssets([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setAssetsLoading(true);
      try {
        const data = await assetService.searchAssets({
          foremanAreaId: Number(foremanAreaId),
          assetType,
        });
        if (!cancelled) setAssets(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setAssets([]);
      } finally {
        if (!cancelled) setAssetsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, serviceAreaId, foremanAreaId, assetType]);

  const isValid =
    context?.canPromote &&
    foremanAreaId !== '' &&
    Number(foremanAreaId) > 0 &&
    assetId !== '' &&
    Number(assetId) > 0 &&
    comments.trim().length > 0 &&
    !!priority &&
    !!location;

  const handleSubmit = async () => {
    if (!row || !isValid || !location) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await promotePcrToWorkId(row.id, {
        foremanAreaId: Number(foremanAreaId),
        comments: comments.trim(),
        priority,
        assetId: Number(assetId),
        latitude: location.lat,
        longitude: location.lng,
      });
      setPromoteResult({
        submission: result.submission,
        workIdId: result.workId.id,
        workIdentificationId: result.workId.workIdentificationId ?? null,
      });
      setStep('statusPrompt');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create Work ID');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChoice = async (moveToInternalReview: boolean) => {
    if (!promoteResult) return;
    if (moveToInternalReview) {
      setApplyingStatus(true);
      setStatusError(null);
      try {
        await patchPcrSubmissionStatus(String(promoteResult.submission.id), {
          status: 'internal_review',
        });
      } catch (e) {
        setStatusError(e instanceof Error ? e.message : 'Failed to update status');
        setApplyingStatus(false);
        return;
      }
      setApplyingStatus(false);
    }
    onSuccess(promoteResult);
    setStep('form');
    setPromoteResult(null);
    onClose();
  };

  if (!visible || (!row && step === 'form')) return null;

  // Step 2: status prompt after Work ID is created
  if (step === 'statusPrompt' && promoteResult) {
    const workLabel = promoteResult.workIdentificationId ?? `#${promoteResult.workIdId}`;
    return (
      // `autoHeight`: two buttons and a couple of lines of text don't need the standard
      // fixed-height treatment — without it, `lg` pins to 85vh on desktop and renders as a
      // mostly-empty sheet (the "work ID modals have a lot of whitespace" report).
      <Modal show onHide={() => void handleStatusChoice(false)} size="lg" autoHeight>
        <Modal.Header closeButton>
          <Modal.Title>Work ID created – set PCR status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-text-center tw-py-2">
            <div className="tw-mb-3">
              <i
                className="bi bi-check-circle-fill tw-text-success"
                style={{ fontSize: '2.25rem' }}
                aria-hidden
              />
            </div>
            <p className="tw-mb-1 tw-font-semibold">
              Work ID <span className="tw-text-primary">{workLabel}</span> created
            </p>
            <p className="tw-text-content-muted tw-text-sm tw-mb-4">
              The Work ID is ready. How should this PCR be classified?
            </p>

            {statusError && (
              <Alert variant="danger" className="tw-text-sm tw-mb-3">{statusError}</Alert>
            )}

            <div className="tw-flex tw-justify-center tw-gap-3 tw-flex-wrap">
              <Button
                variant="outline-primary"
                disabled={applyingStatus}
                onClick={() => void handleStatusChoice(true)}
                style={{ minWidth: 160 }}
              >
                {applyingStatus ? (
                  <>
                    <Spinner size="sm" animation="border" className="tw-mr-2" />
                    Applying…
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-right-circle tw-mr-2" aria-hidden />
                    Internal Review
                  </>
                )}
              </Button>
              <Button
                variant="success"
                disabled={applyingStatus}
                onClick={() => void handleStatusChoice(false)}
                style={{ minWidth: 160 }}
              >
                <i className="bi bi-check2-all tw-mr-2" aria-hidden />
                Keep as Finalized
              </Button>
            </div>

            <p className="tw-text-content-muted tw-text-sm tw-mt-4 tw-mb-0">
              <Link to="/work-ids" target="_blank" rel="noreferrer">
                Open Work IDs
              </Link>{' '}
              to view the new Work ID. WRAP stores when that Work ID record was created for internal reporting only – not as
              a contractual service-area response-time clock.
            </p>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={() => !submitting && onClose()}
      title="Convert PCR to Work ID"
      size="large"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid || submitting || loadingContext}
          >
            {submitting ? 'Creating…' : 'Create Work ID'}
          </Button>
        </>
      }
    >
      <p className="tw-text-content-muted tw-text-sm tw-mb-3">
        After you confirm, a Work ID is created from this PCR and the PCR is finalized. WRAP saves the{' '}
        <strong>Work ID creation timestamp</strong> (when this record is first stored) for internal dashboards and
        operational reporting. That moment is <strong>not</strong> the same as contractual service-area response-time
        obligations. Review and adjust the prefilled fields from the PCR before submitting.
      </p>

      {loadingContext ? (
        <div className="tw-text-content-muted tw-py-3">Loading…</div>
      ) : null}
      {error && !context?.canPromote ? (
        <Alert variant="warning">
          {error}
        </Alert>
      ) : null}
      {error && context?.canPromote ? (
        <Alert variant="danger">
          {error}
        </Alert>
      ) : null}

      {context?.canPromote ? (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Foreman area <span className="tw-text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={foremanAreaId === '' ? '' : String(foremanAreaId)}
                  onChange={(e) => {
                    setForemanAreaId(e.target.value ? Number(e.target.value) : '');
                    setAssetId('');
                  }}
                  required
                >
                  <option value="">Select foreman area</option>
                  {context.filterOptions.foremanAreas.map((fa) => (
                    <option key={fa.id} value={fa.id}>
                      {fa.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Priority <span className="tw-text-danger">*</span>
                </Form.Label>
                <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
                  <option value="">Select priority</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="tw-mt-1">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Work description (from PCR; editable) <span className="tw-text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="tw-mt-1">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Asset type</Form.Label>
                <Form.Select
                  value={assetType}
                  onChange={(e) => {
                    setAssetType(e.target.value);
                    setAssetId('');
                  }}
                >
                  {(Object.values(ASSET_TYPES) as string[]).map((type) => (
                    <option key={type} value={type}>
                      {ASSET_TYPE_LABELS[type as keyof typeof ASSET_TYPE_LABELS] ?? type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Asset <span className="tw-text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={assetId === '' ? '' : String(assetId)}
                  onChange={(e) => setAssetId(e.target.value ? Number(e.target.value) : '')}
                  disabled={!serviceAreaId || assetsLoading}
                >
                  <option value="">
                    {!serviceAreaId
                      ? 'Select a foreman area first'
                      : assetsLoading
                        ? 'Loading assets…'
                        : 'Select asset'}
                  </option>
                  {serviceAreaId &&
                    !assetsLoading &&
                    assets.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} (#{a.assetNumber})
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="tw-mt-3">
            <Form.Label>
              Location / GPS <span className="tw-text-danger">*</span>
            </Form.Label>
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
              <div className="tw-text-sm tw-text-content-muted">
                Location is prefilled from the asset when possible. Adjust the pin to match the site.
                {location ? (
                  <>
                    {' '}
                    <span className="tw-text-content-muted">
                      (Lat {location.lat.toFixed(5)}, Lng {location.lng.toFixed(5)})
                    </span>
                  </>
                ) : null}
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowMap((prev) => !prev)}
              >
                {showMap ? 'Hide map' : 'Show map'}
              </Button>
            </div>
            {showMap ? (
              <GoogleGPSMap
                startLocation={location ?? undefined}
                endLocation={location ?? undefined}
                onLocationSelect={(lat, lng) => setLocation({ lat, lng })}
                locationType="start"
                isPointActivity
                autoFitBounds={false}
                height={260}
              />
            ) : null}
            {!location ? (
              <div className="tw-text-danger tw-text-sm tw-mt-1">Set a location on the map to continue.</div>
            ) : null}
          </div>

          <div className="tw-text-sm tw-text-content-muted tw-mt-3">
            Tip: open the new Work ID from{' '}
            <Link to="/work-ids" target="_blank" rel="noreferrer">
              Work IDs
            </Link>{' '}
            after creation – the detail view shows elapsed time from that Work ID&apos;s creation timestamp (WRAP record
            time only).
          </div>
        </Form>
      ) : null}
    </ResponsiveModal>
  );
};

export default PcrPromoteToWorkIdModal;
